import type { AgendaOccurrence, ProjectRowView } from '@/shared/types/domain';

/**
 * A mesclagem que a lista do dia do Calendário precisa (spec-calendar.md §5.1).
 *
 * **Ocorrência de agenda fixa não é projeto** — não tem situação, responsável,
 * tempo de espera nem tela de detalhe. Mas a lista é ordenada por hora, e omitir
 * o culto de um domingo faria a lista mentir sobre o dia que a grade logo acima
 * mostra: quem lê veria o retiro às 19h como a única coisa marcada, num domingo
 * em que o Templo está ocupado desde as 18h.
 *
 * Função pura, testada: quem ordena não pode depender de quem chamou ter
 * ordenado antes — o sintoma seria uma linha fora de lugar no meio da lista, e
 * ninguém reporta isso como bug.
 */
export type DayRow =
  | { kind: 'project'; project: ProjectRowView }
  | { kind: 'occurrence'; occurrence: AgendaOccurrence };

/**
 * Projetos e ocorrências intercalados por horário de início.
 *
 * **Sem hora vem antes**, e o critério é de leitura, não de conveniência: o que
 * não tem hora marcada é o que enquadra o dia inteiro, e enterrá-lo no fim
 * obrigaria a varrer a lista para descobrir que ele existe. Empate de horário
 * mantém a ordem em que os itens chegaram (`Array.prototype.sort` é estável
 * desde o ES2019), o que põe o projeto antes da ocorrência — a lista é de
 * projeto, e a agenda fixa é o pano de fundo dela.
 */
export function mergeDayRows(
  projects: readonly ProjectRowView[],
  occurrences: readonly AgendaOccurrence[],
): readonly DayRow[] {
  const rows: DayRow[] = [
    ...projects.map((project): DayRow => ({ kind: 'project', project })),
    ...occurrences.map((occurrence): DayRow => ({ kind: 'occurrence', occurrence })),
  ];

  return rows.sort((a, b) => startOf(a) - startOf(b));
}

function startOf(row: DayRow): number {
  const value = row.kind === 'project' ? row.project.starts_at : row.occurrence.starts_at;
  return value ? Date.parse(value) : Number.NEGATIVE_INFINITY;
}
